import { useCallback, useMemo } from 'react'
import { query, where, doc, collection, serverTimestamp } from 'firebase/firestore'
import type { User } from 'firebase/auth'
import { addDoc, deleteDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { setPostsCol } from '../lib/converters'
import { useFirestoreCollection } from './useFirestore'
import { useFriendIds } from './useFriendIds'
import { useBlockedUsers } from '../contexts/BlockedUsersContext'
import { runMutation } from '../lib/firestoreMutation'
import { detectMedia } from '../lib/media'
import { track } from '../lib/analytics'
import type { Group, Set as FestivalSet, SetPost, MediaProvider } from '../types'
import { fbt } from 'fbtee'

export interface SetPostSummary {
  count: number
  providers: Set<MediaProvider>
}

export interface CreateSetPostInput {
  setId?: string      // set-level post
  artistId?: string   // lineup-level post (fans out to all that artist's sets)
  text?: string
  mediaUrl?: string
  audienceGroupIds?: string[]
  audienceFriends?: boolean
}

// Everything the schedule/lineup needs to render feeds, threaded from EventPage.
export interface SetFeed {
  postsBySet: Map<string, SetPost[]>
  postsByArtist: Map<string, SetPost[]>
  summaryBySet: Map<string, SetPostSummary>
  summaryByArtist: Map<string, SetPostSummary>
  allPosts: SetPost[]
  createPost: (input: CreateSetPostInput) => Promise<void>
  deletePost: (postId: string) => void
  groups: Group[]
  isGuest: boolean
  onRequireAuth: () => void
}

const EMPTY_POSTS: SetPost[] = []
const EMPTY_MAP: Map<string, SetPostSummary> = new Map()

function postMillis(p: SetPost): number {
  return p.createdAt?.toMillis?.() ?? 0
}

function sortPosts(arr: SetPost[]): SetPost[] {
  return arr.sort((a, b) => postMillis(b) - postMillis(a))
}

function buildSummary(map: Map<string, SetPost[]>): Map<string, SetPostSummary> {
  if (map.size === 0) return EMPTY_MAP
  const out = new Map<string, SetPostSummary>()
  for (const [key, arr] of map) {
    const providers = new Set<MediaProvider>()
    for (const p of arr) if (p.mediaProvider) providers.add(p.mediaProvider)
    out.set(key, { count: arr.length, providers })
  }
  return out
}

// Set feed: all posts for one event, filtered by visibility. Lineup-level posts
// (artistId) fan out into every set that artist plays — mirrors how artist
// interests fan out to set interests. Privacy is enforced client-side.
export function useSetPosts(
  eventId: string,
  user: User | null,
  groups: Group[],
  linkedGroups: Group[],
  friendIds: string[],
  sets: FestivalSet[],
) {
  const ref = user ? query(setPostsCol(), where('eventId', '==', eventId)) : null
  const { data, loading, error } = useFirestoreCollection<SetPost>(ref)

  // An event's feed is its crew, not everyone you have ever shared a group
  // with: members of groups linked to *this* event, plus explicit friends. A
  // post carrying an explicit audience skips this — the author addressed you.
  const peerIds = useFriendIds(user, linkedGroups, friendIds)
  const { isBlocked } = useBlockedUsers()

  // Build a map from artistId → setIds for fan-out.
  const artistToSetIds = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const s of sets) {
      for (const aId of s.artistIds) {
        const arr = m.get(aId)
        if (arr) arr.push(s.id)
        else m.set(aId, [s.id])
      }
    }
    return m
  }, [sets])

  const visible = useMemo(() => {
    if (!user) return EMPTY_POSTS
    const peers = new Set(peerIds)
    const myGroupIds = new Set(groups.map(g => g.id))
    const friends = new Set(friendIds)
    return data.filter(p => {
      if (p.hidden) return false
      if (isBlocked(p.authorId)) return false
      if (p.authorId === user.uid) return true
      const restricted = (p.audienceGroupIds?.length ?? 0) > 0 || p.audienceFriends === true
      if (!restricted) return peers.has(p.authorId)
      if (p.audienceGroupIds?.some(id => myGroupIds.has(id))) return true
      if (p.audienceFriends && friends.has(p.authorId)) return true
      return false
    })
  }, [data, user, peerIds, groups, friendIds, isBlocked])

  // postsBySet: direct set posts + artist posts fanned out.
  const postsBySet = useMemo(() => {
    const map = new Map<string, SetPost[]>()
    for (const p of visible) {
      if (p.setId) {
        const arr = map.get(p.setId)
        if (arr) arr.push(p)
        else map.set(p.setId, [p])
      }
      if (p.artistId) {
        const setIds = artistToSetIds.get(p.artistId) ?? []
        for (const sid of setIds) {
          const arr = map.get(sid)
          if (arr) arr.push(p)
          else map.set(sid, [p])
        }
      }
    }
    for (const arr of map.values()) sortPosts(arr)
    return map
  }, [visible, artistToSetIds])

  // postsByArtist: lineup-level posts only (not fanned-out set posts).
  const postsByArtist = useMemo(() => {
    const map = new Map<string, SetPost[]>()
    for (const p of visible) {
      if (!p.artistId) continue
      const arr = map.get(p.artistId)
      if (arr) arr.push(p)
      else map.set(p.artistId, [p])
    }
    for (const arr of map.values()) sortPosts(arr)
    return map
  }, [visible])

  const allPosts = useMemo(() => sortPosts([...visible]), [visible])

  const summaryBySet = useMemo(() => buildSummary(postsBySet), [postsBySet])
  const summaryByArtist = useMemo(() => buildSummary(postsByArtist), [postsByArtist])

  const createPost = useCallback(
    async (input: CreateSetPostInput) => {
      if (!user) return
      const text = input.text?.trim()
      const mediaUrl = input.mediaUrl?.trim()
      if (!text && !mediaUrl) return
      if (!input.setId && !input.artistId) return
      const provider = mediaUrl ? detectMedia(mediaUrl).provider : null
      await runMutation(fbt("Couldn't post", 'Error toast when publishing a set feed post fails'), async () => {
        await addDoc(collection(db, 'setPosts'), {
          authorId: user.uid,
          eventId,
          ...(input.setId ? { setId: input.setId } : {}),
          ...(input.artistId ? { artistId: input.artistId } : {}),
          ...(text ? { text } : {}),
          ...(mediaUrl ? { mediaUrl, mediaProvider: provider } : {}),
          ...(input.audienceGroupIds?.length ? { audienceGroupIds: input.audienceGroupIds } : {}),
          ...(input.audienceFriends ? { audienceFriends: true } : {}),
          createdAt: serverTimestamp(),
        })
        track('set_post_created', {
          event_id: eventId,
          kind: text && mediaUrl ? 'text_media' : mediaUrl ? 'media' : 'text',
          media_provider: provider ?? 'none',
          audience: input.audienceGroupIds?.length || input.audienceFriends ? 'restricted' : 'all',
        })
      })
    },
    [user, eventId],
  )

  const deletePost = useCallback(async (postId: string) => {
    await runMutation(fbt("Couldn't delete post", 'Error toast when deleting a set feed post fails'), async () => {
      await deleteDoc(doc(db, 'setPosts', postId))
    })
  }, [])

  return { postsBySet, postsByArtist, allPosts, summaryBySet, summaryByArtist, loading, error, createPost, deletePost }
}
